// ═══════════════════════════════════════════════════════════════════════
// WeConnect CRM — Funciones utilitarias puras
// ═══════════════════════════════════════════════════════════════════════
// Funciones que NO dependen de React ni de estado.
// Antes vivían todas sueltas dentro de App.js (líneas 115-465).
// Ahora cualquier archivo las importa desde acá.

import {
  LOCALIDADES_REFERENCIA,
  PALABRAS_NO_DIRECCION,
  FRASES_NO_NOMBRE,
  DEFAULT_SALES_SUMMARY,
  API_URL,
} from './constants';

// ── Helpers de texto ─────────────────────────────────────────────────

export function normalizarTexto(texto = '') {
  return String(texto || '').trim();
}

export function normalizarTelefonoVisual(telefono = '') {
  return String(telefono || '').replace(/\D/g, '');
}

export function normalizarComparable(texto = '') {
  return String(texto || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function capitalizarCadaPalabra(texto = '') {
  return String(texto || '')
    .toLowerCase()
    .replace(/\w/g, (c) => c.toUpperCase())
    .replace(/Y/g, 'y');
}

export function formatDateTimeLocal(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
}

export function formatearEntreCalles(calle1 = '', calle2 = '') {
  const a = normalizarTexto(calle1);
  const b = normalizarTexto(calle2);
  if (a && b) return `${a} y ${b}`;
  if (a) return a;
  if (b) return b;
  return '';
}

// ── Validaciones ─────────────────────────────────────────────────────

export function esDocumentoValido(valor = '') {
  const digits = String(valor || '').replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 11;
}

export function esLocalidadValida(localidad = '', direccion = '') {
  if (!localidad || String(localidad).trim().length < 3) return false;
  const texto = String(localidad).trim().toLowerCase();
  const basura = ['si', 'no', 'ok', 'dale', 'bien', 'gracias', 'hola', 'buenas', 'perfecto'];
  if (basura.includes(texto)) return false;
  const esConocida = LOCALIDADES_REFERENCIA.some(
    (loc) => texto.includes(loc) || loc.includes(texto)
  );
  if (esConocida) return true;
  if (texto.length >= 4 && !/^\d+$/.test(texto)) return true;
  return false;
}

export function esProbableDireccion(contenido = '') {
  const texto = String(contenido || '').trim();
  if (!texto) return false;
  if (texto.includes('@')) return false;
  if (/^\d{10,13}$/.test(texto.replace(/\D/g, ''))) return false;
  if (!/\d/.test(texto) || !/[a-záéíóúñ]/i.test(texto)) return false;

  const normalizado = texto.toLowerCase();
  if (PALABRAS_NO_DIRECCION.some((palabra) => normalizado.includes(palabra))) {
    const tieneCalleConNumero = /[a-záéíóúñ]{3,}.*\d{1,5}/i.test(texto);
    const mencionaLocalidad = LOCALIDADES_REFERENCIA.some((loc) => normalizado.includes(loc));
    if (!tieneCalleConNumero || !mencionaLocalidad) return false;
  }

  return /[a-záéíóúñ]{3,}.*\d{1,5}/i.test(texto);
}

export function mensajePrevioPideDireccion(textoPrevio = '') {
  const previo = String(textoPrevio || '').toLowerCase();
  return (
    (previo.includes('dirección') || previo.includes('direccion')) &&
    (previo.includes('localidad') || previo.includes('cobertura'))
  );
}

// ── Extractores desde mensajes ───────────────────────────────────────
// Leen la lista de mensajes del chat y extraen datos del cliente.

export function extraerEmailDesdeMensajes(listaMensajes = []) {
  for (const msg of listaMensajes) {
    const contenido = String(msg?.contenido || '');
    const match = contenido.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    if (match) return match[0];
  }
  return '';
}

export function extraerDocumentoDesdeMensajes(listaMensajes = []) {
  let candidato = '';
  for (let i = listaMensajes.length - 1; i >= 0; i -= 1) {
    const msg = listaMensajes[i];
    if (msg?.rol !== 'cliente') continue;
    const contenido = String(msg?.contenido || '').replace(/\D/g, '');
    if (!esDocumentoValido(contenido)) continue;

    const previo = normalizarComparable(listaMensajes[i - 1]?.contenido || '');
    if (previo.includes('cuil') || previo.includes('dni')) return contenido;
    if (!candidato) candidato = contenido;
  }
  return candidato;
}

export function extraerDireccionDesdeMensajes(listaMensajes = []) {
  let candidataFinal = '';

  const limpiarDireccionCliente = (texto) => {
    let t = String(texto || '').trim();

    const patrones = [
      /(?:cobertura|servicio|internet)\s+(?:en|para|por)\s+(.+)$/i,
      /(?:en|para)\s+([A-Za-z0-9\s.,°º-]*\d+[A-Za-z0-9\s.,°º-]*)$/i,
      /([A-Za-z0-9\s.,°º-]*\d+[A-Za-z0-9\s.,°º-]*)$/i,
    ];

    for (const patron of patrones) {
      const match = t.match(patron);
      if (match && match[1]) {
        t = match[1].trim();
        break;
      }
    }

    return t
      .replace(/^(hola|buenas|buen dia|buenos dias|quiero|quisiera|necesito)\s+/i, '')
      .replace(/[?!.]+$/g, '')
      .trim();
  };

  for (let i = 0; i < listaMensajes.length; i += 1) {
    const actual = listaMensajes[i];
    if (actual?.rol !== 'cliente') continue;

    const contenido = String(actual?.contenido || '').trim();
    if (!esProbableDireccion(contenido)) continue;

    const direccionLimpia = limpiarDireccionCliente(contenido);
    if (!direccionLimpia || !/\d/.test(direccionLimpia)) continue;

    const previo = String(listaMensajes[i - 1]?.contenido || '');
    if (mensajePrevioPideDireccion(previo)) {
      return direccionLimpia;
    }

    candidataFinal = direccionLimpia;
  }

  return candidataFinal;
}

export function extraerNombreCompletoDesdeMensajes(listaMensajes = []) {
  for (let i = 0; i < listaMensajes.length; i += 1) {
    const actual = listaMensajes[i];
    const contenido = String(actual?.contenido || '').trim();
    if (actual?.rol !== 'cliente') continue;
    if (!contenido || contenido.includes('@') || /\d/.test(contenido)) continue;
    const palabras = contenido.split(/\s+/);
    if (palabras.length < 2) continue;
    // Rechazar intersecciones de calles: "Alsina y Amancio Alcorta"
    const hayY = palabras.some(
      (p, idx) => idx > 0 && idx < palabras.length - 1 && p.toLowerCase() === 'y'
    );
    if (hayY) continue;
    // Rechazar si contiene palabras típicas de direcciones
    const textoBajo = contenido.toLowerCase();
    const esDireccion =
      PALABRAS_NO_DIRECCION.some((p) => textoBajo.includes(p)) ||
      /\b(calle|av\.?|avenida|pasaje|blvd|entre|esquina)\b/i.test(contenido);
    if (esDireccion) continue;
    // Rechazar frases que son confirmaciones, no nombres
    const textoNorm = textoBajo
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
    if (FRASES_NO_NOMBRE.some((f) => textoNorm === f || textoNorm.startsWith(f + ' '))) continue;
    // Requerir que el previo sea pregunta explícita de nombre
    const previo = String(listaMensajes[i - 1]?.contenido || '').toLowerCase();
    const preguntoNombre =
      previo.includes('nombre y apellido') ||
      previo.includes('nombre completo') ||
      previo.includes('tu nombre') ||
      (previo.includes('titular') && previo.includes('?'));
    if (preguntoNombre) {
      return contenido
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }
  }
  return '';
}

export function extraerPlanDesdeMensajes(listaMensajes = []) {
  for (let i = listaMensajes.length - 1; i >= 0; i -= 1) {
    const contenido = String(listaMensajes[i]?.contenido || '').toLowerCase();

    if (/\b600\s*(mb|mega|megas)\b/.test(contenido)) return '600 Mb';
    if (/\b300\s*(mb|mega|megas)\b/.test(contenido)) return '300 Mb';
    if (/\b100\s*(mb|mega|megas)\b/.test(contenido)) return '100 Mb';
    if (/\b50\s*(mb|mega|megas)\b/.test(contenido)) return '50 Mb';
  }

  return '';
}

export function extraerPrecioDesdeMensajes(listaMensajes = []) {
  for (let i = listaMensajes.length - 1; i >= 0; i -= 1) {
    const contenido = String(listaMensajes[i]?.contenido || '');
    const match = contenido.match(/\$\s*([\d.]+)/);
    if (match) return match[1].replace(/\./g, '');
  }
  return '';
}

export function extraerMedioPagoDesdeMensajes(listaMensajes = []) {
  for (let i = listaMensajes.length - 1; i >= 0; i -= 1) {
    const contenido = String(listaMensajes[i]?.contenido || '').toLowerCase();
    if (contenido.includes('mercado')) return 'Mercado Pago';
    if (contenido.includes('transfer')) return 'Transferencia';
    if (contenido.includes('pago fácil') || contenido.includes('pagofacil')) return 'Pago Fácil';
    if (contenido.includes('tarjeta')) return 'Tarjeta';
  }
  return '';
}

// ── Inferencia de localidad desde dirección ──────────────────────────

export function inferirLocalidadDesdeDireccion(direccion = '') {
  const texto = String(direccion || '').trim().toLowerCase();
  if (!texto) return '';

  let mejorLocalidad = '';
  let mejorPos = -1;

  for (const localidad of LOCALIDADES_REFERENCIA) {
    const pos = texto.lastIndexOf(localidad);
    if (pos !== -1 && pos > mejorPos) {
      mejorPos = pos;
      mejorLocalidad = localidad;
    }
  }

  return mejorLocalidad ? capitalizarCadaPalabra(mejorLocalidad) : '';
}

export function limpiarDireccionSinLocalidad(direccion = '', localidad = '') {
  let texto = String(direccion || '').trim();
  const candidatas = [localidad, ...LOCALIDADES_REFERENCIA.map(capitalizarCadaPalabra)].filter(
    Boolean
  );
  for (const candidata of candidatas) {
    const escapada = String(candidata).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`([,\\s]+)${escapada}$`, 'i');
    texto = texto
      .replace(regex, '')
      .trim()
      .replace(/,$/, '')
      .trim();
  }
  return texto;
}

// ── Geocodificación frontend ─────────────────────────────────────────

export async function geocodificarResumenVenta(
  { direccion = '', entre_calle_1 = '', entre_calle_2 = '', localidad = '' } = {},
  authFetch
) {
  try {
    const response = await authFetch(`${API_URL}/api/dashboard/ventas/geocodificar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ direccion, entre_calle_1, entre_calle_2, localidad }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) return null;
    return {
      coordenadas: data?.coordenadas || null,
      direccion_canonica: data?.direccion_canonica || '',
      localidad_canonica: data?.localidad_canonica || '',
      display_name: data?.display_name || '',
    };
  } catch (error) {
    console.error('Error geocodificando resumen venta:', error);
    return null;
  }
}

// ── Constructor del formulario de venta ──────────────────────────────
// Combina datos extraídos del chat con datos guardados previamente.

export function crearFormularioVentaInicial({
  conversacion,
  mensajes = [],
  datosCliente = null,
  ventaActual = null,
  resumenGuardado = {},
  agenteNombre = '',
}) {
  const emailDetectado = extraerEmailDesdeMensajes(mensajes);
  const cuilDetectado = extraerDocumentoDesdeMensajes(mensajes);
  const direccionDetectada = extraerDireccionDesdeMensajes(mensajes);
  const direccionBase = esProbableDireccion(direccionDetectada)
    ? direccionDetectada
    : esProbableDireccion(resumenGuardado.direccion)
    ? resumenGuardado.direccion
    : '';
  const localidadDetectadaChat = inferirLocalidadDesdeDireccion(direccionBase);
  const localidadGuardadaValida = esLocalidadValida(resumenGuardado.localidad, direccionBase)
    ? capitalizarCadaPalabra(resumenGuardado.localidad)
    : '';
  const localidadDetectada = localidadDetectadaChat || localidadGuardadaValida;
  const direccion = limpiarDireccionSinLocalidad(direccionBase, localidadDetectada);
  const email = emailDetectado || resumenGuardado.email || '';
  const cuil = esDocumentoValido(cuilDetectado)
    ? cuilDetectado
    : esDocumentoValido(resumenGuardado.cuil)
    ? resumenGuardado.cuil
    : '';
  const telConv = conversacion?.telefono || '';
  const esIdSocial = telConv.startsWith('fb_') || telConv.startsWith('ig_');
  const telefono = normalizarTelefonoVisual(
    resumenGuardado.telefono || (esIdSocial ? '' : telConv) || ''
  );
  const plan = extraerPlanDesdeMensajes(mensajes) || resumenGuardado.plan || ventaActual?.planSugerido || '';
  const precio = extraerPrecioDesdeMensajes(mensajes) || resumenGuardado.precio || '';
  const medioPago = extraerMedioPagoDesdeMensajes(mensajes) || resumenGuardado.medio_pago || '';
  const nombreDetectado =
    extraerNombreCompletoDesdeMensajes(mensajes) ||
    resumenGuardado.nombre_apellido ||
    conversacion?.nombre_cliente ||
    '';

  return {
    ...DEFAULT_SALES_SUMMARY,
    ...resumenGuardado,
    nombre_apellido: nombreDetectado,
    cuil,
    email,
    direccion,
    entre_calle_1: resumenGuardado.entre_calle_1 || '',
    entre_calle_2: resumenGuardado.entre_calle_2 || '',
    localidad: localidadDetectada,
    telefono,
    plan,
    precio,
    medio_pago: medioPago,
    latitud: resumenGuardado.latitud || '',
    longitud: resumenGuardado.longitud || '',
    mapa_url: resumenGuardado.mapa_url || '',
    observaciones: resumenGuardado.observaciones || '',
    agente_nombre: agenteNombre || '',
  };
}

// ── Auth fetch helper ────────────────────────────────────────────────
// Agrega el token JWT a todos los requests al dashboard.

export function authFetch(url, options = {}) {
  const token = localStorage.getItem('wc_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}


